import { ThemeProvider } from "@material-tailwind/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as env from "env-var";
import { useCallback, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import { ApiError, NOT_AUTHENTICATED_STATUS_CODES, NOT_CACHED_STATUS_CODE } from "./api/types";
import { appConfig } from "./appConfig";
import { authProvider } from "./appGlobals";
import ButtonDefault from "./components/Button/ButtonDefault";
import InstallApp from "./components/InstallApp/InstallApp";
import ManagedRoute from "./components/ManagedRoute";
import AuthContext from "./contexts/AuthContext/AuthProvider";
import { UIProvider } from "./contexts/UIContext/UIProvider";
import "./i18n";
import { useTranslation } from "react-i18next";
import { useDialog } from "~/src/hooks/useDialog";
import { useViewportVars } from "./hooks/useViewportVars";
import Debug from "./pages/Debug/Debug";
import Login from "./pages/Login/Login";
import RequireDataLayout from "./pages/MainLayout/RequireDataLayout";
import { registerServiceWorker } from "./service-worker/init";
import { materialTailwindThemeOverrides, muiOverrides } from "./theme";

registerServiceWorker(appConfig);

const App = (): JSX.Element => {
  const { t } = useTranslation();
  const serverfeilDialog = useDialog({ title: t("actions.error") });
  const displayErrorDialog = useCallback((apiError: ApiError) => {
    let dialogContent = {
      title: t("dialogs.serverfeil.title"),
      message: t("dialogs.serverfeil.message"),
    };
    serverfeilDialog.show({
      ...dialogContent,
      buttons: (
        <ButtonDefault
          id="serverfeilDialogLukkButton"
          label={t("actions.close")}
          onClick={() => {
            serverfeilDialog.close();
          }}
        />
      ),
    });
  }, []);

  const queryCache = new QueryCache({
    onError: (error: Error) => {
      if ("statusCode" in error) {
        const apiError = error as ApiError;
        if (apiError.statusCode === 500) {
          displayErrorDialog(apiError);
        }
      }
    },
  });

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: queryCache,
        defaultOptions: {
          queries: {
            staleTime: appConfig.reactQuery.defaultStaleTime.seconds || 5 * 1000,
            gcTime: appConfig.reactQuery.defaultGCTime || Infinity,
            networkMode: appConfig.reactQuery.networkMode,
            retry: (failureCount, error) => {
              if (window.location.pathname === "/login" || authProvider.isRefreshing) {
                return false;
              }
              if ("statusCode" in error) {
                const apiError = error as ApiError;
                if (
                  NOT_AUTHENTICATED_STATUS_CODES.includes(apiError.statusCode) ||
                  apiError.statusCode === NOT_CACHED_STATUS_CODE
                ) {
                  return false;
                }
              }
              return failureCount <= appConfig.reactQuery.maxRetries;
            },
          },
        },
      }),
  );

  //listens for 'new data'-messages from service worker, and lets relevant listening queries know to refetch
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type !== "CACHE_UPDATED") return;

      const partialPath: string | undefined = data?.partialPath; // e.g. "/jegeropplysninger"
      if (!partialPath) return;

      // Invalidate queries where meta.url matches the partial path
      queryClient.invalidateQueries({
        predicate: (q) => q.meta?.url === partialPath,
        refetchType: "active", // refetch only observed queries
      });
    };

    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [queryClient]);

  useViewportVars();

  return (
    <div className="App mx-auto max-w-screen-max text-ldir-brown-900 shadow-xl">
      {serverfeilDialog.element}
      <QueryClientProvider client={queryClient}>
        <AuthContext.Provider value={authProvider}>
          <ThemeProvider value={materialTailwindThemeOverrides}>
            <MuiThemeProvider theme={muiOverrides}>
              <UIProvider>
                <>
                  <InstallApp />
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    {env.get("NODE_ENV").asString() === "development" && (
                      <Route path="/debug" element={<Debug />} />
                    )}

                    <Route path="/*" element={<ManagedRoute element={<RequireDataLayout />} />} />
                  </Routes>
                </>
              </UIProvider>
            </MuiThemeProvider>
          </ThemeProvider>
        </AuthContext.Provider>
      </QueryClientProvider>
    </div>
  );
};

export default App;
